import { useCallback, useState } from 'react';
import type { SnoozeFormState, SnoozeRequestBody } from 'src/admin/common/components/SnoozeModal/types';
import { SNOOZE_REASONS } from 'src/admin/common/components/SnoozeModal/constants';
import useOpenCloseToggle from 'src/common/hooks/useOpenCloseToggle';
import bulkSnoozeNotifications from 'src/admin/listing-editor/utils/bulkSnoozeNotifications';
import filterPublishedListings from 'src/admin/listing-editor/utils/api/filterPublishedListings';
import type { MinimalEditorListing } from 'src/common/types/api/listings';
import bulkSnoozeListings from '../utils/api/bulkSnoozeListings';
import bulkUnsnoozeListings from '../utils/api/bulkUnsnoozeListings';

type UseListingSnoozeParams = {
  onSubmit?: (enabled: boolean, values: SnoozeFormState) => void;
};

type UseListingSnoozeReturn = {
  submit: (values: SnoozeFormState) => void;
  snoozeBulkListings: (ids: string[]) => void;
  snoozeIndividualListing: (id: string) => void;
  isIndividualListing: boolean;
  selectedListingIds: string[];
  filterListings: (headline: string) => Promise<MinimalEditorListing[]>;
  closeModal: () => void;
  openModal: () => void;
  isModalOpen: boolean;
};

/**
 * The modal state is not part of a context. Don't use this hook in a parent and
 * child component or the modal won't open and close correctly.
 *
 * Affected properties: `closeModal`, `openModal`, `isModalOpen`
 */
const useListingSnooze = ({
  onSubmit,
}: UseListingSnoozeParams = {}): UseListingSnoozeReturn => {
  const snoozeListings = useCallback(
    async (data: SnoozeRequestBody) => bulkSnoozeListings(data),
    [],
  );

  const unsnoozeListings = useCallback(
    async (listingIds: string[]) => bulkUnsnoozeListings(listingIds),
    [],
  );

  const filterListings = useCallback(
    async (headline: string) => {
      const response = await filterPublishedListings(headline);
      return response;
    },
    [],
  );

  const {
    open: openModal,
    close: closeModal,
    isOpen: isModalOpen,
  } = useOpenCloseToggle();

  const [selectedListingIds, setSelectedListingIds] = useState<string[]>([]);
  const [isIndividualListing, setIndividualSnooze] = useState(false);

  const snoozeIndividualListing = useCallback((id: string) => {
    setSelectedListingIds([id]);
    setIndividualSnooze(true);
    openModal();
  }, [openModal]);

  const snoozeBulkListings = useCallback((ids: string[]) => {
    setSelectedListingIds(ids);
    setIndividualSnooze(false);
    openModal();
  }, [openModal]);

  const submit = useCallback(async (values: SnoozeFormState) => {
    const { snooze_enabled: snoozeEnabled, ...data } = values;
    if (snoozeEnabled) {
      // Set a default reason if not provided.
      data.snooze_reason = data.snooze_reason ?? SNOOZE_REASONS.OTHER;

      const result = await snoozeListings(data);
      bulkSnoozeNotifications(!result.success, 'snooze', values);
    }
    if (!snoozeEnabled) {
      const result = await unsnoozeListings(data.boats);
      bulkSnoozeNotifications(!result.success, 'unsnooze', values);
    }
    closeModal();
    onSubmit?.(snoozeEnabled, values);
  }, [closeModal, onSubmit, snoozeListings, unsnoozeListings]);

  return {
    submit,
    selectedListingIds,
    snoozeBulkListings,
    snoozeIndividualListing,
    filterListings,
    isIndividualListing,
    openModal,
    closeModal,
    isModalOpen,
  };
};

export default useListingSnooze;
